import {
  createBatchEventProcessor,
  createForwardingEventProcessor,
  createInstance,
  createLogger,
  createStaticProjectConfigManager,
  DEBUG,
  ERROR,
} from '@optimizely/optimizely-sdk';
import type { Client } from '@optimizely/optimizely-sdk';
import { useState, defineNuxtPlugin, useRoute } from 'nuxt/app';
import { useOptimizelyState } from '../composables/useOptimizelyState';
import { isLocalStorageUsable } from '../utils/local-storage';

export default defineNuxtPlugin(async (): Promise<{ provide: { optimizely: Client | null } }> => {
  const route = useRoute();

  const { optimizelyDisabled } = useOptimizelyState();

  if (optimizelyDisabled) {
    return {
      provide: {
        optimizely: null,
      },
    };
  }

  /**
   * Empty when Optimizely is disabled server-side (e.g. the TAPP/CI user agent) or when the
   * server client was not ready. We still build an instance from it: it buckets nobody, exactly
   * as v5 did with an absent datafile, and keeping `$optimizely` non-null is what allows
   * `useOptimizely` to reach the `optimizely_force_variation` query short-circuit.
   */
  const datafile = useState<string>('optimizely-datafile').value ?? '';

  const optimizelyClient = createInstance({
    // use the same datafile on both client and server
    projectConfigManager: createStaticProjectConfigManager({ datafile }),

    /**
     * Since v6 the SDK dispatches no events at all unless an event processor is passed in
     * explicitly. Without this, `activate()` still buckets the user and still returns the right
     * variation, but the impression never reaches Optimizely — a failure that is invisible from
     * the browser. (The data layer is fed separately, by the getAllFeatureVariables decision.)
     * @see useVariation, which calls `activate` specifically to create those impressions.
     *
     * The batch processor needs a working `localStorage`, which Android WebViews with DOM storage
     * disabled do not have (TAPP-3078).
     */
    eventProcessor: isLocalStorageUsable() ? createBatchEventProcessor() : createForwardingEventProcessor(),

    /**
     * Since v6 logging is off unless a logger is passed in, and passing none is not the same as
     * v5's default: v5 installed a logger at module load and ran it at ERROR, so SDK errors did
     * reach the console. Without this the browser silently swallows malformed-datafile,
     * unknown-key and event-dispatch failures — including via Bugsnag, since the SDK reports
     * them through the logger rather than by throwing.
     */
    logger: createLogger({ level: route.query.optimizely_log === 'true' ? DEBUG : ERROR }),
  });

  return {
    provide: {
      optimizely: optimizelyClient,
    },
  };
});
